import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import Button from 'reactstrap/lib/Button';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildNewTmvPricingPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { VisitorModel } from 'client/data/models/visitor';
import { getUnifiedLeadFormDealerCountV2Path, UnifiedLeadFormModel } from 'client/data/models/unified-lead-form';
import { LEAD_FORM_CONFIGS } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { getEspLabel } from 'site-modules/shared/utils/core-page/suggested-price';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { FeaturesSpecsCtaUI } from 'site-modules/features-specs/components/features-specs-cta/features-specs-cta';

import './new-pricing.scss';

function NewPricingUI({
  className = null,
  vehicle,
  styleId,
  trimName = null,
  tmvData = null,
  pageName = null,
  zipCode = null,
  city = null,
  isMobile = false,
  hasLeadFormDealers = false,
}) {
  const [isDescriptionExpanded, toggleDescriptionExpanded] = useToggle(false);

  if (!tmvData) {
    return null;
  }

  const totalMsrp = get(tmvData, 'totalWithOptions.baseMSRP');
  const baseMsrp = get(tmvData, 'nationalBasePrice.baseMSRP');
  const ESP = Math.max(get(tmvData, 'totalWithOptions.tmv', 0) - get(tmvData, 'incentivesAndRebates'), 0);
  const displayPrice = ESP || totalMsrp || baseMsrp;
  const espLabel = getEspLabel({ suggestedPrice: ESP, totalMsrp });

  const expandButtonProps = {
    onClick: toggleDescriptionExpanded,
    color: 'link',
    size: 'sm',
    className: 'text-underline text-blue-40 p-0 text-transform-none fw-normal',
    'data-tracking-id': 'expand_explainer_text',
  };

  const trimSummary = {
    hasLeadFormDealers,
    id: styleId,
    trim: trimName,
    suggestedPrice: ESP,
  };

  return (
    <div
      className={classnames('new-pricing rounded-12 bg-cool-gray-90 px-1 py-1', className)}
      data-tracking-parent="features-specs-pricing"
    >
      <h2 className="heading-4 mb-1">Pricing</h2>
      <div className="rounded-8 bg-white pt-1_5 pb-1 px-1">
        <div className="text-center fw-medium mb-0_25">{espLabel}</div>
        <div className="text-center heading-2 mb-1">{getPriceString(displayPrice)}</div>
        <div className="small mb-1_5">
          <div className={classnames('description-text', { collapsed: !isDescriptionExpanded })}>
            {ESP <= totalMsrp ? (
              <Fragment>
                The <strong>Edmunds Suggested Price</strong> is what we recommend you pay, including destination fee but
                excluding options, taxes, and other fees. It&apos;s based on our analysis of millions of data points,
                including supply, demand, incentives, and recent nearby transactions.
              </Fragment>
            ) : (
              'Low supply is pushing the market average above MSRP.'
            )}
          </div>
          {ESP <= totalMsrp && (
            <Fragment>
              <div className={classnames({ 'd-none': isDescriptionExpanded })}>
                <Button {...expandButtonProps}>more</Button>
              </div>
              <div className={classnames({ 'd-none': !isDescriptionExpanded })}>
                <Button {...expandButtonProps}>less</Button>
              </div>
            </Fragment>
          )}
        </div>
        <div className="text-center">
          <FeaturesSpecsCtaUI
            vehicle={vehicle}
            styleId={styleId}
            trimSummary={trimSummary}
            leadFormCreativeId={getLeadFormCreativeId({
              isNewVin: true,
              pageNameLegacy: pageName,
              formName: LEAD_FORMS_CREATIVE_ID.FEATURE_SPECS,
            })}
            dataTrackingParent="features-specs-cta1"
            zipCode={zipCode}
            city={city}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
}

NewPricingUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  styleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  trimName: PropTypes.string,
  className: PropTypes.string,
  tmvData: VehicleEntities.NewTmv,
  pageName: PropTypes.string,
  zipCode: PropTypes.string,
  city: PropTypes.string,
  hasLeadFormDealers: PropTypes.bool,
  isMobile: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  pageName: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
});

export const propsAreEqual = (prevProps, { tmvData }) => !tmvData;
export const NewPricing = connect(mapStateToProps)(
  connectToModel(React.memo(NewPricingUI, propsAreEqual), {
    tmvData: bindToPath(({ styleId }) => buildNewTmvPricingPath(styleId, false), VehicleModel),
    hasLeadFormDealers: bindToPath(
      ({ vehicle }) =>
        getUnifiedLeadFormDealerCountV2Path({
          makeSlug: vehicle.make.slug,
          modelSlug: vehicle.model.slug,
          strategy: LEAD_FORM_CONFIGS.QUICK_QUOTES_TRIM_DST.strategy,
        }),
      UnifiedLeadFormModel,
      dealerCount => !!dealerCount
    ),
    zipCode: bindToPath('location.zipCode', VisitorModel),
    city: bindToPath('location.city', VisitorModel),
  })
);
